import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { validatePassword } from '../../utils/userAccount';
import { validateEmail, sanitizeEmailInput } from '../../utils/emailValidation';
import { apiService } from '../../utils/api';
import { API_BASE_URL } from '../../config/api';

interface InviteData {
  valid: boolean;
  inviteeEmail?: string;
  inviteeName?: string;
  estateOwnerName?: string;
  error?: string;
}

type InviteFlowMode = 'login' | 'signup';

const Frame129: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useSessionPersistence();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  // Invite token handling
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [inviteRole, setInviteRole] = useState<'executor' | 'beneficiary' | null>(null);
  const [isValidatingInvite, setIsValidatingInvite] = useState(false);

  // For invite flows, default to login to prevent duplicates for existing users
  const [inviteFlowMode, setInviteFlowMode] = useState<InviteFlowMode>('signup');

  // Check for invite token in URL and validate it
  useEffect(() => {
    const token = searchParams.get('invite');
    if (token) {
      setInviteToken(token);
      setInviteFlowMode('login');
      validateInviteToken(token);
    } else {
      setInviteToken(null);
      setInviteData(null);
      setInviteFlowMode('signup');
    }
  }, [searchParams]);

  // Check for Wix referral data in URL (ref parameter)
  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam) {
      try {
        const decoded = atob(decodeURIComponent(refParam));
        const data = JSON.parse(decoded);
        console.log('[Frame129] Pre-filling from Wix referral:', data);
        if (data.e) setEmail(sanitizeEmailInput(data.e).toLowerCase().trim());
        if (data.f) setFirstName(data.f.trim());
        if (data.l) setLastName(data.l.trim());
      } catch (error) {
        console.error('[Frame129] Failed to decode ref param:', error);
      }
    }
  }, [searchParams]);

  // Check for direct query parameters from 3rd party lead providers
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const firstParam = searchParams.get('first');
    const lastParam = searchParams.get('last');

    if (emailParam || firstParam || lastParam) {
      if (emailParam) setEmail(decodeURIComponent(emailParam).toLowerCase().trim());
      if (firstParam) setFirstName(decodeURIComponent(firstParam).trim());
      if (lastParam) setLastName(decodeURIComponent(lastParam).trim());
    }
  }, [searchParams]);

  const validateInviteToken = async (token: string) => {
    setIsValidatingInvite(true);
    try {
      const execResp = await fetch(`${API_BASE_URL}/api/executors/validate-invite/${token}`);
      const execData = await execResp.json();

      let role: 'executor' | 'beneficiary' | null = null;
      let inviteeEmail: string | undefined;
      let inviteeName: string | undefined;
      let estateOwnerName: string | undefined;
      let valid = false;
      let inviteError: string | undefined;

      if (execData.valid) {
        role = 'executor';
        inviteeEmail = execData.executorEmail;
        inviteeName = execData.executorName;
        estateOwnerName = execData.estateOwnerName;
        valid = true;
      } else {
        const beneResp = await fetch(`${API_BASE_URL}/api/beneficiaries/validate-invite/${token}`);
        const beneData = await beneResp.json();

        if (beneData.valid) {
          role = 'beneficiary';
          inviteeEmail = beneData.beneficiaryEmail;
          inviteeName = beneData.beneficiaryName;
          estateOwnerName = beneData.estateOwnerName;
          valid = true;
        } else {
          inviteError = execData.error || beneData.error || 'Invalid invite token';
        }
      }

      setInviteRole(role);
      setInviteData(valid ? { valid: true, inviteeEmail, inviteeName, estateOwnerName } : { valid: false, error: inviteError });

      if (valid) {
        if (inviteeEmail) setEmail(sanitizeEmailInput(inviteeEmail).toLowerCase().trim());
        if (inviteeName) {
          const nameParts = inviteeName.split(' ');
          setFirstName(nameParts[0] ?? '');
          setLastName(nameParts.slice(1).join(' '));
        }
      }
    } catch (err) {
      console.error('[Frame129] Failed to validate invite:', err);
      setInviteData({ valid: false, error: 'Failed to validate invite' });
    } finally {
      setIsValidatingInvite(false);
    }
  };

  const acceptInvite = async (accountToken: string) => {
    if (!inviteToken || !inviteRole) return;
    const endpoint = inviteRole === 'beneficiary'
      ? '/api/beneficiaries/accept-invite'
      : '/api/executors/accept-invite';

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accountToken}`
        },
        body: JSON.stringify({ inviteToken })
      });
      if (response.ok) {
        console.log(`[Frame129] Successfully accepted ${inviteRole} invite`);
      } else {
        console.error('[Frame129] Failed to accept invite:', await response.json());
      }
    } catch (err) {
      console.error('[Frame129] Error accepting invite:', err);
    }
  };

  const goToLoginToAcceptInvite = () => {
    if (!inviteToken) return;
    const qp = new URLSearchParams();
    qp.set('invite', inviteToken);
    if (email) qp.set('email', email);
    if (inviteRole) qp.set('role', inviteRole);
    navigate(`/login?${qp.toString()}`);
  };

  const passwordValidation = validatePassword(password);
  const passwordsMatch = password === confirmPassword;
  const emailValidation = validateEmail(email);

  const isComplete =
    email && emailValidation.isValid &&
    firstName && lastName &&
    password && confirmPassword &&
    passwordValidation.isValid && passwordsMatch;

  const canProceedToLogin = Boolean(inviteToken && !!email && emailValidation.isValid);

  const isLoginMode = inviteToken && inviteData?.valid && inviteFlowMode === 'login';

  const inputClass =
    'w-full h-11 bg-white border border-neutral-300 rounded-lg px-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-700';

  return (
    <div className="min-h-screen bg-white-alt flex flex-col">

      {/* Two-column main content */}
      <main className="flex-1 flex items-start justify-center gap-12 px-8 pb-16 pt-10 max-w-5xl mx-auto w-full">

        {/* ── Left: Logo + Form ── */}
        <div className="w-full max-w-md flex flex-col gap-4">
          {/* Logo — centered above form card */}
          <div className="flex justify-center">
            <BrandSymbol size="3xl" showText={true} variant="dark" />
          </div>
          {/* Form card */}
          <div className="bg-[#f0f0f0] rounded-2xl p-8 space-y-5">

            {/* Invite validation spinner */}
            {isValidatingInvite && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-cyan-800 flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin shrink-0" />
                Validating your invitation…
              </div>
            )}

            {/* Valid invite banner */}
            {inviteData?.valid && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 space-y-3">
                <p className="text-cyan-900 font-medium text-sm">
                  You&apos;ve been designated as {inviteRole === 'beneficiary' ? 'a beneficiary' : 'an executor'}
                </p>
                <p className="text-cyan-700 text-xs leading-relaxed">
                  {inviteData.estateOwnerName} has designated you. If you already have an account, log in to accept (prevents duplicates).
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setInviteFlowMode('login')}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                      inviteFlowMode === 'login'
                        ? 'bg-cyan-700 text-white border-cyan-700'
                        : 'bg-white text-cyan-800 border-cyan-200 hover:border-cyan-400'
                    }`}
                  >
                    I have an account
                  </button>
                  <button
                    type="button"
                    onClick={() => setInviteFlowMode('signup')}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                      inviteFlowMode === 'signup'
                        ? 'bg-cyan-700 text-white border-cyan-700'
                        : 'bg-white text-cyan-800 border-cyan-200 hover:border-cyan-400'
                    }`}
                  >
                    Create new account
                  </button>
                </div>
              </div>
            )}

            {/* Invalid invite banner */}
            {inviteData && !inviteData.valid && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {inviteData.error || 'This invitation link is invalid or has already been used.'}
              </div>
            )}

            {/* Heading */}
            <h2 className="text-2xl font-semibold text-[#1a3a4a] text-center">
              {isLoginMode ? 'Log in to accept your invitation.' : 'Sign Up'}
            </h2>

            {/* Email */}
            <div className="space-y-1">
              <input
                id="email"
                type="email"
                className={inputClass}
                placeholder="Your Email"
                value={email}
                aria-invalid={!!email && !emailValidation.isValid}
                onChange={(e) => {
                  const sanitized = sanitizeEmailInput(e.target.value);
                  setEmail(sanitized.toLowerCase().trim());
                }}
              />
              {email && !emailValidation.isValid && (
                <div className="text-red-600 text-xs space-y-0.5 pl-1">
                  {emailValidation.errors.map((err, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-red-400">•</span>{err}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Signup-only fields */}
            {!isLoginMode && (
              <>
                {/* First / Last Name */}
                <div className="flex gap-3">
                  <input
                    id="first"
                    type="text"
                    className={inputClass}
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <input
                    id="last"
                    type="text"
                    className={inputClass}
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>

                {/* Password / Confirm */}
                <div className="flex gap-3">
                  <div className="flex-1 space-y-1">
                    <input
                      id="password"
                      type="password"
                      className={inputClass}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    {password && !passwordValidation.isValid && (
                      <div className="text-red-600 text-xs space-y-0.5 pl-1">
                        {passwordValidation.errors.map((err, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <span className="text-red-400">•</span>{err}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-1">
                    <input
                      id="confirmPassword"
                      type="password"
                      className={inputClass}
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    {confirmPassword && !passwordsMatch && (
                      <div className="text-red-600 text-xs pl-1 flex items-center gap-1">
                        <span className="text-red-400">•</span>Passwords do not match
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Error */}
            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {/* Primary action button */}
            {isLoginMode ? (
              <button
                disabled={!canProceedToLogin || isCreating}
                onClick={() => { setError(''); goToLoginToAcceptInvite(); }}
                className={`w-full py-3 rounded-lg bg-[#067681] text-white font-semibold text-sm tracking-wide transition-colors ${
                  canProceedToLogin && !isCreating ? 'hover:bg-[#055c67]' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                Log In to Accept Invite →
              </button>
            ) : (
              <button
                disabled={!isComplete || isCreating}
                onClick={async () => {
                  setIsCreating(true);
                  setError('');
                  try {
                    const result = await apiService.createAccount({
                      email,
                      name: `${firstName} ${lastName}`,
                      password
                    });

                    if (result.success) {
                      if (inviteToken && result.token) {
                        try {
                          await acceptInvite(result.token);
                          console.log('[Frame129] Successfully linked executor invite');
                        } catch (inviteErr) {
                          console.error('[Frame129] Failed to accept invite:', inviteErr);
                        }
                      }
                      sessionStorage.setItem('pending_verification_email', email);
                      const isExecutor = inviteToken ? 'true' : 'false';
                      navigate(`/verify-email-pending?email=${encodeURIComponent(email)}&executor=${isExecutor}`);
                    } else {
                      setError(result.message ?? 'Failed to create account. Please try again.');
                    }
                  } catch (err) {
                    setError('Failed to create account. Please try again.');
                    console.error('Account creation error:', err);
                  } finally {
                    setIsCreating(false);
                  }
                }}
                className={`w-full py-3 rounded-lg bg-[#067681] text-white font-semibold text-sm tracking-wide transition-colors ${
                  isComplete && !isCreating ? 'hover:bg-[#055c67]' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                {isCreating ? 'Creating Account…' : 'Create Account'}
              </button>
            )}

            {isLoginMode && (
              <p className="text-xs text-stone-500 text-center">You&apos;ll accept the invitation after logging in.</p>
            )}
          </div>

          {/* Below-card links */}
          <div className="text-center space-y-2">
            <p className="text-sm text-stone-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-[#067681] hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
            <p className="text-xs text-stone-400 leading-relaxed">
              By creating an account, you agree to our{' '}
              <a
                href="https://www.endofanera.ai/company/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#067681] hover:underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://www.endofanera.ai/company/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#067681] hover:underline"
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>

        {/* ── Right: Marketing panel ── */}
        <div className="hidden md:flex w-full max-w-lg flex-col items-center justify-start pt-[144px] gap-6">
          {/* Tagline */}
          <p className="text-stone-600 text-base leading-relaxed max-w-sm text-center">
            Your one secure place for everything that matters. Whether you&apos;re preparing your own legacy or
            supporting an aging parent, End of an Era is the simplest way to ensure nothing is lost.
          </p>

          {/* Hero image (pills are baked into the image) */}
          <img
            src="/assets/images/signup-hero.png"
            alt="Estate planning made simple"
            className="w-full max-w-md rounded-2xl"
          />
        </div>

      </main>
    </div>
  );
};

export default Frame129;
